// Work is in progress on this file
import React from 'react';
import { Tooltip, Menu, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import { useNavigate } from 'react-router-dom';
import EmptyState from '../../EmptyState';
import { EyeFilled, MoreOutlined } from '@ant-design/icons';
import { CustomShipmentText } from '@/components';
import { getNotificationListTriggerType } from '@/common/utils/notificationUtils';
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import { useUserDateTimeFormat } from '@/hooks';
import {
  getCocCommentAnchorId,
  useScrollToCocComment,
} from '../../../ControlTower/hooks/useScrollToCocComment';

interface TeamUser {
  enabled?: boolean;
  name?: string;
  email?: string;
  id?: string | number;
}

interface TeamItem {
  users?: TeamUser[];
  [key: string]: unknown;
}

interface AdditionalUser {
  enabled?: boolean;
  label?: string;
  key?: string;
  [key: string]: unknown;
}

interface NotificationSendTo {
  teams_users?: TeamItem[];
  additional?: AdditionalUser[];
  [key: string]: unknown;
}

export interface NotificationItem {
  id: number | string;
  alert_type?: string;
  title?: string;
  sub_title?: string;
  send_to?: NotificationSendTo;
  triggered?: string;
  [key: string]: unknown;
}

interface NotificationListSectionProps {
  title?: string;
  list: NotificationItem[];
  isCompanyLevel: boolean;
  onCreate?: () => void;
  onEdit?: (item: NotificationItem) => void;
  onDelete?: (item: NotificationItem) => void;
  /** Notification to scroll to and highlight (e.g. from the activity feed). */
  highlightId?: string;
}

const NotificationListSection: React.FC<NotificationListSectionProps> = ({
  list,
  isCompanyLevel,
  onCreate,
  onEdit,
  onDelete,
  highlightId,
}) => {
  const navigate = useNavigate();
  const hasHighlightRow = Boolean(
    highlightId && list?.some((item) => String(item.id) === highlightId)
  );
  // Scroll the notification into view and highlight it until the next click,
  // reusing the CoC comment behaviour (rows carry the CoC anchor id below).
  useScrollToCocComment({ commentId: highlightId, ready: hasHighlightRow });
  const { formatDateTime } = useUserDateTimeFormat();

  const formatDate = (iso?: string) => {
    if (!iso) return '—';

    return formatDateTime(iso, {
      includeTime: true,
    });
  };

  const handleMenuClick = ({
    key,
    item,
  }: {
    key: string;
    item: NotificationItem;
  }) => {
    if (key === 'edit') {
      onEdit?.(item);
    } else if (key === 'delete') {
      onDelete?.(item);
    }
  };

  const allColumns: TableColumnsType<NotificationItem> = [
    {
      title: 'Type / delivery',
      key: 'type_delivery',
      width: '150px',
      render: (_: unknown, item: NotificationItem) => {
        const triggerInfo = getNotificationListTriggerType(
          item as unknown as Parameters<
            typeof getNotificationListTriggerType
          >[0]
        );
        const title = triggerInfo?.title;
        const formatted = triggerInfo?.formatted;
        const tooltipList = triggerInfo?.tooltipList;
        const moreText = triggerInfo?.moreText;

        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              {item?.alert_type === 'CRITICAL_ALERT' ||
              item?.alert_type === 'PICKUP_DELIVERY' ? (
                <span className='font-12 fw-400'>
                  {' '}
                  {formatted ? `${title}:${formatted}` : `${title}`}
                  {moreText && (
                    <Tooltip
                      title={tooltipList}
                      classNames={{ root: 'font-12' }}
                    >
                      <span style={{ cursor: 'pointer' }}>{moreText}</span>
                    </Tooltip>
                  )}
                </span>
              ) : (
                <CustomShipmentText text={item?.title || '—'} fontSize='14px' />
              )}
            </div>
            <TruncateWithTooltip
              text={String(item?.sub_title || '')}
              max={25}
              render={(t) => (
                <span
                  style={{
                    width: 'fit-content',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <span>{t}</span>
                </span>
              )}
            />
          </div>
        );
      },
    },
    {
      title: 'Recipients',
      key: 'users',
      width: '150px',
      render: (_: unknown, item: NotificationItem) => {
        const teamUsers = (
          Array.isArray(item?.send_to?.teams_users)
            ? item.send_to.teams_users
            : []
        ).flatMap((team: TeamItem) =>
          Array.isArray(team?.users)
            ? team.users
                .filter((u: TeamUser) => u?.enabled !== false)
                .map(
                  (u: TeamUser) => u?.name || u?.email || String(u?.id || '')
                )
            : []
        );

        const additionalUsers = Array.isArray(item?.send_to?.additional)
          ? item.send_to.additional
          : [];

        const filteredAdditionalUsers = additionalUsers
          .filter((obj: AdditionalUser) => obj?.enabled !== false)
          .map((obj: AdditionalUser) => obj?.label || obj?.key || '');

        const allUsers = [...teamUsers, ...filteredAdditionalUsers].filter(
          Boolean
        );

        // Build display
        const content =
          allUsers.length > 0 ? (
            <>
              {allUsers[0]}
              {allUsers.length > 1 && (
                <>
                  ,{' '}
                  <Tooltip title={allUsers.slice(1).join(', ')}>
                    <span
                      style={{
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                        textUnderlineOffset: '3px',
                        cursor: 'pointer',
                      }}
                    >
                      +{allUsers.length - 1} more
                    </span>
                  </Tooltip>
                </>
              )}
            </>
          ) : (
            '—'
          );

        return (
          <div>
            <div>{content}</div>
          </div>
        );
      },
    },
    {
      title: isCompanyLevel ? 'Last triggered' : 'Triggered',
      key: 'triggered',
      width: '100px',
      render: (_: unknown, item: NotificationItem) => {
        const text = formatDate(item?.triggered) || '-';
        return (
          <div>
            <CustomShipmentText text={text} lineHeight='20px' />
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      width: '16px',
      render: (_: unknown, item: NotificationItem) => {
        const menu = (
          <Menu
            onClick={({ key }) => handleMenuClick({ key: String(key), item })}
          >
            <Menu.Item key='edit'>Edit</Menu.Item>
            <Menu.Item key='delete'>Delete</Menu.Item>
          </Menu>
        );
        return (
          <div>
            {isCompanyLevel ? (
              <Tooltip title='View in settings'>
                <EyeFilled
                  onClick={() => navigate('/settings/notification')}
                  style={{ fontSize: 16, cursor: 'pointer' }}
                />
              </Tooltip>
            ) : (
              <Dropdown overlay={menu} trigger={['hover']}>
                <MoreOutlined style={{ fontSize: 16, cursor: 'pointer' }} />
              </Dropdown>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      {list?.length > 0 ? (
        <div className='settings-content-wrapper'>
          <Table
            columns={allColumns}
            dataSource={list}
            rowKey='id'
            onRow={(item) => ({ id: getCocCommentAnchorId(String(item.id)) })}
            pagination={false}
            rowHoverable={false}
            className='settings-table notification-drawer-table'
          />
        </div>
      ) : (
        <EmptyState
          message={
            isCompanyLevel ? 'None exist yet. Create one' : 'None exist yet'
          }
          linkText={isCompanyLevel ? 'here.' : ''}
          fontWeight={600}
          onClick={
            isCompanyLevel ? () => navigate('/settings/notification') : onCreate
          }
        />
      )}
    </>
  );
};

export default NotificationListSection;
